import {useState} from "react";
import {saveSpot} from "../../api/spot/spot.api";
import SuspensePopup from "../SuspensePopup";
import {Alert, RadioGroup, TextInput} from "omoplay";
import TreeChoiceForm from "./TreeChoiceForm";
import QREditor from "./QREditor";

// 진입점 주소 → TreeChoiceForm 선택 표현(주소 선택 > 시도 > 시도 시군구)
//
// 🔴 첫 항목('주소 선택')은 데이터가 아니라 TreeChoiceForm 이 3-depth 고정이라서 두는 **껍데기**다.
//    종전 라벨이 '배송지 주소' 였는데 진입점과 무관한 용어였다(그 컴포넌트를 배송지 필터에서
//    먼저 쓴 흔적). 2026-08-27 '주소 선택' 으로 정정.
//    ⚠️ 저장되는 값(`spot.address`)은 여전히 **"시도 시군구"** 문자열이다 — 이 형식은
//    비오는날 자동 메시지가 `forecast_info(province, city)` 조회에 쓰므로 바꿀 수 없다
//    (AutomateMessageReservedScheduler.findRainydayUser).
const buildAddressFilters = (spot) => spot?.address ? [
  { category: "address", value: "address", label: "주소 선택" },
  { category: "address", value: spot.address.split(" ")[0], label: spot.address.split(" ")[0] },
  { category: "address", value: spot.address, label: spot.address },
] : []

// regionData(지역 트리)는 부모(QRList)에서 1회 조회해 prop 으로 전달 → remount 되어도 항상 준비됨.
// selectedTargetFilters 는 마운트 시점에 동기 초기화해야 TreeChoiceForm 의 onActiveFilter(시도/시군구 활성)가
// 올바르게 초기화됨(useEffect 로 뒤늦게 넣으면 시군구 컬럼이 비어 보임). 스팟 변경 시 remount 는 QRList 의 key 가 담당.
const QREditForm = ({visible, qrSpot, regionData = [], onClose}) => {
  const [spotData, setSpotData] = useState(qrSpot ?? {type: "OFFLINE"})
  const [selectedTargetFilters, setSelectedTargetFilters] = useState(() => buildAddressFilters(qrSpot))
  const [qrShape, setQrShape] = useState({})

  const handleConfirm = () => {
    saveSpot(spotData?.id, spotData.type, spotData.name, spotData.address, qrShape).then(() => {
      window.location.reload()
    })
  }

  return (
      <SuspensePopup
          title={`진입점 ${qrSpot ? '수정' : '추가'}`}
          visible={visible}
          style={{maxHeight: "1080px"}}
          confirmDisabled={!spotData?.name || (spotData?.type !== "ONLINE" && !spotData?.address)}
          onConfirm={() => handleConfirm()}
          confirmText={qrSpot ? '수정' : '추가'}
          onClose={() => onClose()}
      >
        <div>
          <Alert type="info" withIcon={true}
                 title="프로젝트에 연결해 QR 코드 및 URL을 사용할 수 있습니다." />
          <div className="py-4 flex-col gap-8">
            <div className="flex-col gap-8">
              <div className="flex-align-center">
                <label className="text-md font-semibold text-gray-900 relative mr-2">
                  타입
                </label>
                <RadioGroup
                    initChecked={spotData?.type}
                    items={[{label: "오프라인", value: "OFFLINE"}, {label: "온라인", value: "ONLINE"}]}
                    onSelected={(value) => {
                      if (value === "ONLINE") {
                        setSpotData({...spotData, type: value, address: null})
                        setSelectedTargetFilters([])
                      } else {
                        setSpotData({...spotData, type: value})
                      }
                    }}
                />
              </div>
              <div>
                <label className="text-md font-semibold text-gray-900 relative mr-2">
                  진입점명
                </label>
                <TextInput placeholder="진입점명을 설정해 주세요. (ex. 광화문점)"
                           maxLength={20}
                           value={spotData?.name}
                           onChange={(e) => setSpotData({...spotData, name: e.target.value})}
                />
              </div>
              {
                  spotData?.type !== "ONLINE" && (
                      <div>
                        <div>
                          <label className="text-md font-semibold text-gray-900 relative mr-2">
                            지역 선택
                          </label>
                          <TreeChoiceForm isSingle={true}
                                          targetFilters={[{value: "address", label: "주소 선택", data: regionData}]}
                                          selectedTargetFilters={selectedTargetFilters}
                                          onChangeTargetFilters={(targetFilters) => {
                                           setSelectedTargetFilters(targetFilters)
                                           setSpotData({...spotData, address: targetFilters[0]?.value ?? null})
                                       }}
                          />
                        </div>
                      </div>
                  )
              }
            </div>
            {
                qrSpot == null && (
                    <QREditor onChangeType={(type) => setQrShape({...qrShape, type})}
                              onChangeLogo={(logo) => setQrShape({...qrShape, logo})}
                              onChangeUsedUid={(usedUid) => setQrShape({...qrShape, usedUid})}
                              onChangeClearly={(clearly) => setQrShape({...qrShape, clearly})}
                    />
                )
            }
          </div>
        </div>
      </SuspensePopup>
  );
};

export default QREditForm;
